import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import PriceListEditor from './components/PriceListEditor';
import PriceListHistory from './components/PriceListHistory';
import { supabase } from '../../lib/supabase';

const CustomerPriceListManagement = () => {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [priceLists, setPriceLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPriceList, setEditingPriceList] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (selectedCustomerId) {
      fetchPriceLists(selectedCustomerId);
    } else {
      setPriceLists([]);
    }
  }, [selectedCustomerId]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        ?.from('customers')
        ?.select('id, name, email, company')
        ?.eq('status', 'active')
        ?.order('name');

      if (error) throw error;

      setCustomers((data || [])?.map(c => ({
        value: c?.id,
        label: c?.name,
        description: c?.company || c?.email
      })));
    } catch (error) {
      console.error('Error fetching customers:', error?.message);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPriceLists = async (customerId) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        ?.from('customer_price_lists')
        ?.select(`
          *,
          customer:customers(*),
          created_by_user:personnel!customer_price_lists_created_by_fkey(*),
          price_list_items(
            *,
            product:products(*)
          )
        `)
        ?.eq('customer_id', customerId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      setPriceLists(data || []);
    } catch (error) {
      console.error('Error fetching price lists:', error?.message);
      setPriceLists([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNewPriceList = () => {
    setShowEditor(true);
    setEditingPriceList({
      id: null,
      customer_id: selectedCustomerId,
      name: '',
      description: '',
      is_active: true,
      valid_from: new Date()?.toISOString()?.split('T')?.[0],
      valid_until: null,
      price_list_items: []
    });
  };

  const handleSavePriceList = async (priceListData) => {
    try {
      // Validate unique name before saving
      const { data: existingPriceLists, error: checkError } = await supabase
        ?.from('customer_price_lists')
        ?.select('id, name')
        ?.eq('customer_id', selectedCustomerId)
        ?.eq('name', priceListData?.name);

      if (checkError) throw checkError;

      // Check if name conflicts with another price list
      const hasConflict = existingPriceLists?.some(pl => pl?.id !== priceListData?.id);
      
      if (hasConflict) {
        alert(`A price list with the name "${priceListData?.name}" already exists for this customer. Please use a unique name.`);
        return;
      }

      if (priceListData?.id) {
        // Update existing price list
        const { error: updateError } = await supabase
          ?.from('customer_price_lists')
          ?.update({
            name: priceListData?.name,
            description: priceListData?.description,
            is_active: priceListData?.is_active,
            valid_from: priceListData?.valid_from,
            valid_until: priceListData?.valid_until || null
          })
          ?.eq('id', priceListData?.id);

        if (updateError) throw updateError;

        // Delete existing items
        await supabase
          ?.from('price_list_items')
          ?.delete()
          ?.eq('price_list_id', priceListData?.id);

        // Insert new items
        if (priceListData?.items?.length > 0) {
          const { error: itemsError } = await supabase
            ?.from('price_list_items')
            ?.insert(priceListData?.items?.map(item => ({
              price_list_id: priceListData?.id,
              product_id: item?.product_id,
              discount_type: item?.discount_type,
              discount_value: item?.discount_value,
              custom_price: item?.custom_price,
              notes: item?.notes
            })));

          if (itemsError) throw itemsError;
        }
      } else {
        // Create new price list
        const { data: newPriceList, error: priceListError } = await supabase
          ?.from('customer_price_lists')
          ?.insert([{
            name: priceListData?.name,
            customer_id: selectedCustomerId,
            description: priceListData?.description,
            is_active: priceListData?.is_active,
            valid_from: priceListData?.valid_from,
            valid_until: priceListData?.valid_until || null
          }])
          ?.select()
          ?.single();

        if (priceListError) throw priceListError;

        // Insert items
        if (priceListData?.items?.length > 0) {
          const { error: itemsError } = await supabase
            ?.from('price_list_items')
            ?.insert(priceListData?.items?.map(item => ({
              price_list_id: newPriceList?.id,
              product_id: item?.product_id,
              discount_type: item?.discount_type,
              discount_value: item?.discount_value,
              custom_price: item?.custom_price,
              notes: item?.notes
            })));

          if (itemsError) throw itemsError;
        }
      }

      setSuccessMessage('Price list saved successfully!');
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);

      // Refresh data
      fetchPriceLists(selectedCustomerId);
    } catch (error) {
      console.error('Error saving price list:', error?.message);
      
      // Provide specific error messages
      if (error?.message?.includes('duplicate key') || error?.message?.includes('unique constraint')) {
        alert(`A price list with this name already exists for this customer. Please use a unique name.`);
      } else {
        alert(`Failed to save price list: ${error?.message || 'Unknown error'}. Please try again.`);
      }
    }
  };

  const handleSelectPriceList = (priceList) => {
    setShowEditor(true);
    setEditingPriceList(priceList);
  };

  return (
    <>
      <Helmet>
        <title>Customer Price List Management - RIGJOCKEY</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-20 pb-8 px-4 lg:px-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-heading font-bold text-foreground">Customer Price List Management</h1>
                <p className="text-muted-foreground mt-1">Create and manage customer-specific pricing with automated discount rules</p>
              </div>
            </div>

            {/* Success Message */}
            {showSuccessMessage && (
              <div className="bg-success/10 border border-success/20 rounded-lg p-4 flex items-center gap-3">
                <Icon name="CheckCircle" size={20} color="var(--color-success)" />
                <span className="text-success font-medium">{successMessage}</span>
              </div>
            )}

            {/* Customer Selection */}
            <div className="bg-card rounded-xl border border-border shadow-elevation-1 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Users" size={20} color="var(--color-primary)" />
                </div>
                <h2 className="text-xl font-heading font-semibold text-foreground">Select Customer</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Customer"
                  placeholder="Search and select customer"
                  searchable
                  options={customers}
                  value={selectedCustomerId}
                  onChange={setSelectedCustomerId}
                  loading={loading}
                />
                {selectedCustomerId && (
                  <div className="flex items-end">
                    <Button
                      onClick={handleCreateNewPriceList}
                      iconName="Plus"
                      iconPosition="left"
                      fullWidth
                    >
                      Create New Price List
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Price List Editor */}
            {selectedCustomerId && editingPriceList && (
              <PriceListEditor
                priceList={editingPriceList}
                onSave={handleSavePriceList}
                onCancel={() => {
                  setShowEditor(false);
                  setEditingPriceList(null);
                }}
              />
            )}

            {/* Price List History */}
            {selectedCustomerId && priceLists?.length > 0 && (
              <PriceListHistory
                priceLists={priceLists}
                activePriceListId={editingPriceList?.id}
                onSelectPriceList={handleSelectPriceList}
              />
            )}

            {/* Empty State */}
            {!selectedCustomerId && (
              <div className="bg-card rounded-xl border border-border shadow-elevation-1 p-12 text-center">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="DollarSign" size={32} color="var(--color-muted-foreground)" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">No Customer Selected</h3>
                <p className="text-muted-foreground">Select a customer above to view or create their price lists</p>
              </div>
            )}

            {selectedCustomerId && !editingPriceList && priceLists?.length === 0 && (
              <div className="bg-card rounded-xl border border-border shadow-elevation-1 p-12 text-center">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="FileText" size={32} color="var(--color-muted-foreground)" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">No Price Lists Found</h3>
                <p className="text-muted-foreground mb-4">This customer doesn't have any price lists yet</p>
                <Button onClick={handleCreateNewPriceList} iconName="Plus" iconPosition="left">
                  Create First Price List
                </Button>
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default CustomerPriceListManagement;